import { formatNumber } from "$root/localization/formatters";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { selectAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import type { ICellRendererParams } from "ag-grid-community";
import { useMemo, type JSX } from "react";
import { PortfolioLabContext, type PortfolioLabInvestmentItem } from "../../../ctx";
import { isWeightCellEditable } from "../editability";
import type { MergedCompositionRow } from "../rows";

type LabValueCellRendererProps = ICellRendererParams<MergedCompositionRow> & {
	virtualUuid: string;
};

/**
 * Narrow per-column projection of `investments$` for the editability predicates: a single-item
 * list that re-emits ONLY when this column's `compositionSaveStatus` changes. That is the one
 * live input to cell dimming — value changes already reach the cell through the controlled
 * rowData (`getRowId` delta) path, and AUM changes rebuild the rowData too (`compositionEqual`).
 * Subscribing to the full atom instead would repaint every cell on every weight edit.
 */
function useEditabilityInvestments(virtualUuid: string): PortfolioLabInvestmentItem[] {
	const ctx = useMandatoryContext(PortfolioLabContext);
	// Keyed on virtualUuid (unlike the useState-created projections elsewhere): a discarded
	// initializer would only recreate an identical pure derivation, so useMemo is safe here.
	const investments$ = useMemo(
		() =>
			selectAtom(
				ctx.investments$,
				(items) => items.filter((i) => i.virtualUuid === virtualUuid),
				(a, b) => a.length === b.length && a[0]?.compositionSaveStatus === b[0]?.compositionSaveStatus,
			),
		[ctx.investments$, virtualUuid],
	);
	return useAtomValue(investments$);
}

/**
 * Shared dimming for the weight/monetary value cells. Self-subscribing (T3 follow-up): dimming
 * repaints via the narrow save-status projection above instead of an imperative
 * `refreshCells({ force: true })` from the editor component. Both columns dim on the WEIGHT
 * editability predicate on purpose — a monetary cell locked solely by a missing AUM keeps
 * full opacity and explains itself through its tooltip (see `columns.tsx`).
 *
 * A hook rather than a shared wrapper component so each renderer can spell its `data-qualifier`
 * out as a literal — the e2e index only catalogues qualifiers written directly on the attribute.
 */
function useValueCellClassName(props: LabValueCellRendererProps): string {
	const investments = useEditabilityInvestments(props.virtualUuid);
	const dimmed = !isWeightCellEditable({
		virtualUuid: props.virtualUuid,
		row: props.data ?? undefined,
		investments,
	});
	return toClassName({ "opacity-50": dimmed });
}

/**
 * The row a value cell belongs to, as the e2e suite names it (MDOT-24442). It has to be able to read
 * one holding's value out of the grid to prove the table re-seeded after a SphereAI write, and the
 * row LABEL — the instrument name on screen — is the only row identity a test can name: the grid's
 * own `row-id` is the tree path (`getRowId`), which changes with the grouping, and the ticker it ends
 * in never reaches the screen. A label occurs once per column and only inside its own row, so the
 * matches for one label are that row's cells in column order and a locator picks the column by
 * position among them.
 */
function rowKey(props: ICellRendererParams<MergedCompositionRow>): string {
	return props.data?.label ?? "";
}

/** Weight (%) cell: formats with the live `decimals$` precision — repaints itself on change. */
export function LabWeightCellRenderer(props: LabValueCellRendererProps): JSX.Element {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const decimals = useAtomValue(ctx.decimals$);
	const className = useValueCellClassName(props);
	const value = props.value as number | null | undefined;
	const formattedValue = value == null || Number.isNaN(value) ? "N/A" : `${formatNumber(value, decimals)}%`;
	return (
		<span data-qualifier={`PortfolioLab/Composition/Weight(${rowKey(props)})`} className={className}>
			{formattedValue}
		</span>
	);
}

/** Monetary (€) cell: fixed 2 dp, independent of the weight precision stepper. */
export function LabMonetaryCellRenderer(props: LabValueCellRendererProps): JSX.Element {
	const className = useValueCellClassName(props);
	const value = props.value as number | null | undefined;
	const formattedValue = value == null || Number.isNaN(value) ? "N/A" : formatNumber(value, 2);
	return (
		<span data-qualifier={`PortfolioLab/Composition/Monetary(${rowKey(props)})`} className={className}>
			{formattedValue}
		</span>
	);
}

type DeltaKind = "absent" | "zero" | "positive" | "negative";

/** Read off the column's formatter output, which already blanks a delta that rounds to zero. */
function deltaKind(value: number | null | undefined, valueFormatted: string | null | undefined): DeltaKind {
	if (value == null || Number.isNaN(value)) {
		return "absent";
	}
	if (!valueFormatted) {
		return "zero";
	}
	return value > 0 ? "positive" : "negative";
}

/**
 * Δ cell: paints the column's `valueFormatter` output, while the colour stays on the column's `cellStyle`.
 * `data-delta-kind` carries the sign that colour encodes.
 */
export function LabDeltaCellRenderer(props: ICellRendererParams<MergedCompositionRow>): JSX.Element {
	return (
		<span
			data-qualifier={`PortfolioLab/Composition/Delta(${rowKey(props)})`}
			data-delta-kind={deltaKind(props.value as number | null | undefined, props.valueFormatted)}
		>
			{props.valueFormatted}
		</span>
	);
}
